/**
 * Nightly reconciliation of teammates with the client's employee directory
 * (TalentSearchPro/tsp-services#274). recruiter-service owns every rule; the
 * gateway only carries data between it and the integration.
 *
 * `employeeId` is the person's id in the directory (a GUID), not a recruiter
 * or user id.
 */

/** Who switched a recruiter off: an admin, or the employee-directory sync. */
export type DeactivationSource = 'manual' | 'employee-sync';

export interface LinkedRecruiter {
  recruiterId: number;
  employeeId: string;
}

/** Not linked yet: the sync tries to link it by email. */
export interface UnlinkedRecruiter {
  recruiterId: number;
  email: string;
}

/** `GET recruiters/employee-sync/candidates`, for the requester's portal. */
export interface Candidates {
  linked: LinkedRecruiter[];
  unlinked: UnlinkedRecruiter[];
}

/** One person as the directory sees them after the latest sync. */
export interface EmployeeSnapshot {
  employeeId: string;
  /** From the person's main position; `null` when the directory has none. */
  position: string | null;
  /** Every row of the person is dismissed and one carries a resignation date. */
  dismissed: boolean;
  /**
   * The person's functional and line managers on the same row as `position`,
   * as directory person ids — the space of `employeeId`. `null` when the
   * directory names none, or when the gateway predates them
   * (TalentSearchPro/tsp-services#294).
   */
  functionManagerId?: string | null;
  lineManagerId?: string | null;
}

/** `POST recruiters/employee-sync`. */
export interface ApplyPayload {
  /** New links found by email; existing links are not resent. */
  links: LinkedRecruiter[];
  /** Snapshots of every linked person the directory still knows. */
  employees: EmployeeSnapshot[];
}

export interface ApplyResult {
  linked: number;
  positionsUpdated: number;
  deactivated: number;
  reactivated: number;
  /** Too many deactivations in one run: none were applied. */
  deactivationsSkipped: boolean;
  /** Manager suggestions opened by this run. */
  suggestionsCreated: number;
  /** Open suggestions this run closed: the directory changed or dropped the manager. */
  suggestionsClosed: number;
}

/**
 * Manager suggestions (TalentSearchPro/tsp-services#294): the sync proposes a
 * teammate's manager from the directory, a portal admin accepts or rejects.
 * The sync itself never changes a manager.
 */

/** Which directory manager the suggestion came from; the functional one wins. */
export type ManagerSuggestionSource = 'function' | 'line';

/**
 * `superseded` — the sync closed it because the directory changed or dropped
 * the manager; `rejected` — an admin did, and the same pair is not suggested
 * again until the directory names someone else.
 */
export type ManagerSuggestionStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'superseded';

/** A teammate, with both ids named: `recruiters.id` and `users.id` (ADR-0006). */
export interface TeammateRef {
  recruiterId: number;
  userId: number;
}

/** `GET recruiters/manager-suggestions`: one open suggestion. */
export interface ManagerSuggestion {
  id: number;
  teammate: TeammateRef;
  currentManager: TeammateRef | null;
  suggestedManager: TeammateRef;
  source: ManagerSuggestionSource;
  createdAt: string;
  /**
   * Accepting would close a cycle: the suggested manager already reports, up
   * the chain, to the teammate. This is the teammate's direct report on that
   * chain; an admin clears its manager first. `null` when accepting is safe.
   */
  blockedBy: TeammateRef | null;
}
